import React from 'react'
import { useTranslation } from 'react-i18next'
import {
    Card,
    CardContent,
    CardActions,
    Typography,
    Chip,
    Stack,
    Divider,
    Button
} from '@mui/material'
import LocationOnIcon from '@mui/icons-material/LocationOn'
import MedicalServicesIcon from '@mui/icons-material/MedicalServices'
import AccessTimeIcon from '@mui/icons-material/AccessTime'
import dayjs from 'dayjs'
import { toAdapterLocale } from '@/utils/locale'

// Common interface for both shift types
export interface BaseShift {
    startTime: Date | string
    endTime: Date | string
    location: string
    planningName: string
}

// For MyShift type
export interface MyShiftCardProps {
    shift: BaseShift & {
        shiftId: string
        assignedStaffCount: number
        totalStaffCount: number
    }
    type: 'my'
    onAction: (shiftId: string) => void
    actionDisabled?: boolean
}

// For AvailableShift type
export interface AvailableShiftCardProps {
    shift: BaseShift & {
        planningId: string
        shiftTemplateId: string
        availableSpots: number
        totalSpots: number
    }
    type: 'available'
    onAction: (shift: any) => void
    actionDisabled?: boolean
}

type ShiftCardProps = MyShiftCardProps | AvailableShiftCardProps

export function ShiftCard(props: ShiftCardProps) {
    const { t, i18n } = useTranslation()
    const { shift, type, onAction, actionDisabled = false } = props

    // Shift times are true UTC instants (materialized through the template's timezone),
    // so render them on the viewer's local clock: a 09:00 Paris shift shows 09:00 to a
    // Paris doctor and 05:00 to one in Guadeloupe.
    const locale = toAdapterLocale(i18n.language)
    const formattedStartTime = dayjs(shift.startTime).locale(locale).format('HH:mm')
    const formattedEndTime = dayjs(shift.endTime).locale(locale).format('HH:mm')
    const formattedDate = dayjs(shift.startTime).locale(locale).format('dddd D MMM')

    // Determine chip content and color based on shift type
    let chipLabel: string
    let chipColor: 'primary' | 'success' | 'error'

    if (type === 'my') {
        const myShift = shift as MyShiftCardProps['shift']
        chipLabel = `${myShift.assignedStaffCount}/${myShift.totalStaffCount} ${t('page.shiftMarketplace.staff', 'staff')}`
        chipColor = 'primary'
    } else {
        const availableShift = shift as AvailableShiftCardProps['shift']
        chipLabel = `${availableShift.availableSpots}/${availableShift.totalSpots} ${t('page.shiftMarketplace.spots', 'spots')}`
        chipColor = availableShift.availableSpots > 0 ? 'success' : 'error'
    }

    // Determine button properties based on shift type
    let buttonText: string
    let buttonColor: 'primary' | 'error'
    let buttonVariant: 'contained' | 'outlined'

    if (type === 'my') {
        buttonText = t('page.shiftMarketplace.releaseShift', 'Release Shift')
        buttonColor = 'error'
        buttonVariant = 'outlined'
    } else {
        buttonText = t('page.shiftMarketplace.claimShift', 'Claim Shift')
        buttonColor = 'primary'
        buttonVariant = 'contained'
    }

    // Handle button click based on shift type
    const handleActionClick = () => {
        if (type === 'my') {
            onAction((shift as MyShiftCardProps['shift']).shiftId)
        } else {
            onAction(shift)
        }
    }

    return (
        <Card variant="outlined">
            <CardContent>
                <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
                    <Typography variant="h6" component="div">
                        {formattedDate}
                    </Typography>
                    <Chip
                        label={chipLabel}
                        color={chipColor}
                        size="small"
                    />
                </Stack>

                <Divider sx={{ my: 1 }} />
                <Stack spacing={1}>
                    <Stack direction="row" spacing={1} alignItems="center">
                        <AccessTimeIcon fontSize="small" color="action" />
                        <Typography variant="body2">
                            {formattedStartTime} - {formattedEndTime}
                        </Typography>
                    </Stack>

                    <Stack direction="row" spacing={1} alignItems="center">
                        <LocationOnIcon fontSize="small" color="action" />
                        <Typography variant="body2">{shift.location}</Typography>
                    </Stack>

                    <Stack direction="row" spacing={1} alignItems="center">
                        <MedicalServicesIcon fontSize="small" color="action" />
                        <Typography variant="body2">{shift.planningName}</Typography>
                    </Stack>
                </Stack>
            </CardContent>
            <CardActions>
                <Button
                    size="small"
                    variant={buttonVariant}
                    color={buttonColor}
                    fullWidth
                    disabled={actionDisabled}
                    onClick={handleActionClick}
                >
                    {buttonText}
                </Button>
            </CardActions>
        </Card>
    )
}

export default ShiftCard
